/* eslint-disable react-hooks/exhaustive-deps */
import { CheckboxGrid, PrimaryButton } from "../../components";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import { Dispatch, useCallback, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { getWeekRange } from "../../services/dateService";
import { useMarketPricing } from "../../store/useMarketPricing";

interface Props {
  selectedFilters?: string[];
  setSelectedFilters?: Dispatch<React.SetStateAction<string[]>>;
  period: "Daily" | "Weekly" | "LastTwelweMonths" | "Monthly" | "Yearly";
  setPeriod: Dispatch<
    React.SetStateAction<
      "Daily" | "Weekly" | "LastTwelweMonths" | "Monthly" | "Yearly"
    >
  >;
  weeklyRange: Date;
  setWeeklyRange: Dispatch<React.SetStateAction<Date>>;
  monthRange: Date;
  setMonthRange: Dispatch<React.SetStateAction<Date>>;
  yearRange: Date;
  setYearRange: Dispatch<React.SetStateAction<Date>>;
}

export function GeneralFilters({
  selectedFilters,
  setSelectedFilters,
  period,
  setPeriod,
  weeklyRange,
  setWeeklyRange,
  monthRange,
  setMonthRange,
  yearRange,
  setYearRange,
}: Props) {
  /** Hooks */
  const { marketPrices, getAllMarketPrices } = useMarketPricing();
  /** Constants */
  const { startOfWeek, endOfWeek } = getWeekRange(weeklyRange);

  const filterOptions = useMemo(() => {
    const kgOptions = marketPrices?.filter((mp) => mp?.measurement === "kg");
    return [
      "All",
      ...(kgOptions?.length ? kgOptions?.map((price) => price.name) : []),
      "Statiegeld",
      "Total",
    ];
  }, [marketPrices]);

  /** Callbacks */
  const getLastWeek = () =>
    setWeeklyRange(new Date(weeklyRange.setDate(weeklyRange.getDate() - 7)));
  const getNextWeek = () =>
    setWeeklyRange(new Date(weeklyRange.setDate(weeklyRange.getDate() + 7)));

  const getLastMonth = () =>
    setMonthRange(dayjs(monthRange).subtract(1, "month").toDate());
  const getNextMonth = () =>
    setMonthRange(dayjs(monthRange).add(1, "month").toDate());

  const onSelectFilters = useCallback(
    (option: string) => {
      if (!setSelectedFilters || !selectedFilters) return;
      if (option === "All") {
        if (selectedFilters.includes("All")) {
          setSelectedFilters([]);
          return;
        }
        setSelectedFilters([
          "All",
          ...(marketPrices?.length
            ? marketPrices?.map((price) => price.name)
            : []),
          "Statiegeld",
          "Total",
        ]);
        return;
      }
      setSelectedFilters((prev: any) =>
        prev.includes(option)
          ? prev
              .filter((item: any) => item !== option)
              ?.filter((item: any) => item !== "All")
          : [...prev, option]?.filter((item: any) => item !== "All")
      );
    },
    [marketPrices, selectedFilters, filterOptions]
  );

  /** Effects */
  useEffect(() => {
    getAllMarketPrices();
  }, []);

  useEffect(() => {
    if (!setSelectedFilters) return;
    setSelectedFilters([
      "All",
      ...(marketPrices?.length ? marketPrices?.map((price) => price.name) : []),
      "Statiegeld",
      "Total",
    ]);
  }, [marketPrices]);

  return (
    <div>
      {/* General filters */}
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl border-gray-border drop-shadow-none mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <div className="flex flex-row items-start justify-start">
            <p
              className={`font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-12`}
            >
              General filters
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-start justify-start mb-12">
          {period === "Daily" ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0">
              <PrimaryButton size="sm" onClick={getLastWeek} label="Prev" />
              <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                {startOfWeek.toDateString()} - {endOfWeek.toDateString()}
              </div>
              <PrimaryButton
                size="sm"
                disabled={endOfWeek > new Date()}
                onClick={getNextWeek}
                label="Next"
              />
            </div>
          ) : null}
          {period === "Weekly" ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0">
              <PrimaryButton size="sm" onClick={getLastMonth} label="Prev" />
              <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                {dayjs(monthRange).format("MMMM YYYY")}
              </div>
              <PrimaryButton
                size="sm"
                disabled={dayjs(monthRange).add(1, "month").isAfter(dayjs())}
                onClick={getNextMonth}
                label="Next"
              />
            </div>
          ) : null}
          <Menu>
            <MenuHandler>
              <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                {period === "LastTwelweMonths" ? "Last 12 months" : period}
                <ChevronDown className="ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              <MenuItem
                disabled={period === "Daily"}
                onClick={() => setPeriod("Daily")}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Daily
              </MenuItem>
              <MenuItem
                disabled={period === "Weekly"}
                onClick={() => setPeriod("Weekly")}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Weekly
              </MenuItem>
              <MenuItem
                disabled={period === "Monthly"}
                onClick={() => setPeriod("Monthly")}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Monthly
              </MenuItem>
              <MenuItem
                disabled={period === "LastTwelweMonths"}
                onClick={() => setPeriod("LastTwelweMonths")}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Last 12 months
              </MenuItem>
              <MenuItem
                disabled={period === "Yearly"}
                onClick={() => setPeriod("Yearly")}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Yearly
              </MenuItem>
            </MenuList>
          </Menu>
          {period === "Monthly" ? (
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 sm:ml-4 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {yearRange.getFullYear()}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                <MenuItem
                  onClick={() =>
                    setYearRange(
                      new Date(yearRange.setFullYear(new Date().getFullYear()))
                    )
                  }
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {new Date().getFullYear()}
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    setYearRange(
                      new Date(
                        yearRange.setFullYear(new Date().getFullYear() - 1)
                      )
                    )
                  }
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {new Date().getFullYear() - 1}
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    setYearRange(
                      new Date(
                        yearRange.setFullYear(new Date().getFullYear() - 2)
                      )
                    )
                  }
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {new Date().getFullYear() - 2}
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    setYearRange(
                      new Date(
                        yearRange.setFullYear(new Date().getFullYear() - 3)
                      )
                    )
                  }
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {new Date().getFullYear() - 3}
                </MenuItem>
              </MenuList>
            </Menu>
          ) : null}
        </div>
        <CheckboxGrid
          label="Filter by Stream"
          options={filterOptions ?? []}
          selectedOptions={selectedFilters ?? []}
          onSelect={onSelectFilters}
        />
      </div>
    </div>
  );
}
